// One tooltip for the whole page. A control names its words in `data-tip`.
// The browser tooltip of a `title` cannot take the look of the app, so no
// control has a `title`. A touch has no hover, so a touch shows no tooltip.
// The tooltip is a popover, so it is in the top layer of the page. A dialog
// is in that layer too, and an element of the page cannot show over one. The
// tooltip opens again each time it shows, which puts it over an open dialog.

import { TOOLTIP_DELAY_MS, TOOLTIP_GAP_PX } from '../constants';
import { el } from '../lib/dom';
import { createSpellIcon } from './SpellIcon';

const TIP_SELECTOR = '[data-tip]';
const CASTER_SELECTOR = '[data-caster]';
const SHOWN_CLASS = 'tooltip--shown';
const SPELL_CLASS = 'tooltip--spell';

const clamp = (value: number, low: number, high: number): number =>
  Math.min(Math.max(value, low), Math.max(low, high));

export const createTooltip = (): HTMLElement => {
  const root = el('div', { className: 'tooltip', role: 'tooltip' });
  // `manual`: only this code opens and closes it. A click or Escape is for the page.
  root.setAttribute('popover', 'manual');
  const isPopover = typeof root.showPopover === 'function';
  const closePopover = (): void => {
    if (isPopover && root.matches(':popover-open')) root.hidePopover();
  };
  let owner: HTMLElement | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  /** Under the control, or over it when the window has no room under it. */
  const place = (target: HTMLElement): void => {
    const box = target.getBoundingClientRect();
    const tip = root.getBoundingClientRect();
    const left = clamp(
      box.left + box.width / 2 - tip.width / 2,
      TOOLTIP_GAP_PX,
      window.innerWidth - tip.width - TOOLTIP_GAP_PX,
    );
    const under = box.bottom + TOOLTIP_GAP_PX;
    const fitsUnder = under + tip.height + TOOLTIP_GAP_PX <= window.innerHeight;
    const top = fitsUnder ? under : box.top - tip.height - TOOLTIP_GAP_PX;
    root.style.left = `${left}px`;
    root.style.top = `${top}px`;
  };

  /**
   * A control with `data-tip-title` gets the tooltip of a spell: the icon of
   * `data-tip-spell` and the title in the display face, a rule, the words, and
   * `data-tip-note` as a muted last line. Any other control gets its words only.
   */
  const fill = (target: HTMLElement): void => {
    const { tip = '', tipTitle, tipSpell, tipNote } = target.dataset;
    root.classList.toggle(SPELL_CLASS, tipTitle !== undefined);
    // The tooltip is not inside the plate, so it takes the name of the caster to get its colors.
    const caster = target.closest<HTMLElement>(CASTER_SELECTOR)?.dataset.caster;
    if (tipTitle !== undefined && caster) root.dataset.caster = caster;
    else delete root.dataset.caster;
    if (tipTitle === undefined) {
      root.textContent = tip;
      return;
    }
    root.replaceChildren(
      el('div', { className: 'tooltip__head' }, [
        tipSpell ? createSpellIcon(tipSpell, 'tooltip__icon') : null,
        el('span', { className: 'tooltip__title', textContent: tipTitle }),
      ]),
      ...(tip ? [el('p', { className: 'tooltip__words', textContent: tip })] : []),
      ...(tipNote ? [el('p', { className: 'tooltip__note', textContent: tipNote })] : []),
    );
  };

  const hide = (): void => {
    if (timer) clearTimeout(timer);
    timer = null;
    owner = null;
    root.classList.remove(SHOWN_CLASS);
    closePopover();
  };

  const show = (target: HTMLElement): void => {
    if (!target.isConnected || owner !== target) return;
    fill(target);
    // Closed and opened again, it is the last element in the top layer, over a dialog.
    closePopover();
    if (isPopover) root.showPopover();
    place(target);
    root.classList.add(SHOWN_CLASS);
  };

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;
    const target = event.target.closest<HTMLElement>(TIP_SELECTOR);
    if (target === owner) return;
    hide();
    if (!target) return;
    owner = target;
    timer = setTimeout(() => show(target), TOOLTIP_DELAY_MS);
  });
  // A press, a scroll, or a key moves or changes the control under the pointer.
  document.addEventListener('pointerdown', hide);
  document.addEventListener('keydown', hide);
  document.addEventListener('scroll', hide, true);
  document.documentElement.addEventListener('pointerleave', hide);

  return root;
};
